import React, { useState } from 'react';
import { Badge, Box, Button, Flex, Text } from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, GearSix, Key, Plus, Prohibit, Trash, TrashSimple } from 'phosphor-react';
import { toast } from 'sonner';

import { IDataNoPagination, IPagination } from '../../../types/shared';
import { DataTable } from '../../../components/datatable';
import { TableBox } from '../../../components/tableBox';
import { EmptyData } from '../../../components/emptyData';
import { colors } from '../../../theme/colors';
import { ActionsButton } from '../../../components/buttons/ActionsButton';
import { useDialog } from '../../../providers/dialog';
import { IApiToken, useApiController } from '../../../hooks/useApiController';
import { TokenFormModal } from './TokenFormModal';
import { RevelarTokenModal } from './RevelarTokenModal';
import { TokenDetalhesModal } from './TokenDetalhesModal';

const statusColor: Record<string, string> = {
  ativo: 'green',
  revogado: 'red',
  lockout: 'orange',
};

export function TokensTab() {
  const queryClient = useQueryClient();
  const { getTokens, updateToken, deleteToken, deleteTokensRevogados } = useApiController();
  const showDialog = useDialog();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [tokenParaRevelar, setTokenParaRevelar] = useState<IApiToken | null>(null);
  const [detalhes, setDetalhes] = useState<IApiToken | null>(null);

  const { data, isFetching, isError } = useQuery<IDataNoPagination<IApiToken[]>>({
    queryKey: ['api-controller-tokens', {}],
    queryFn: getTokens,
  });

  const invalidar = () => queryClient.invalidateQueries({ queryKey: ['api-controller-tokens'] });

  // Revogar = kill-switch (PUT status=revogado): o token FICA na lista, auditável.
  const { mutateAsync: revogarToken } = useMutation({
    mutationFn: (id: number) => updateToken({ id, data: { status: 'revogado' } }),
    onSuccess: () => {
      invalidar();
      toast.success('Token revogado', { position: 'top-right' });
    },
    onError: () => toast.error('Erro ao revogar token', { position: 'top-right' }),
  });

  // Excluir (lixeira) = soft-delete: some da lista. Só token já revogado.
  const { mutateAsync: excluirToken } = useMutation({
    mutationFn: deleteToken,
    onSuccess: () => {
      invalidar();
      toast.success('Token excluído', { position: 'top-right' });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? 'Erro ao excluir token', {
        position: 'top-right',
      }),
  });

  // Faxina: exclui todos os revogados de uma vez.
  const { mutateAsync: limparRevogados } = useMutation({
    mutationFn: deleteTokensRevogados,
    onSuccess: (res: any) => {
      invalidar();
      const n = res?.data?.excluidos ?? 0;
      toast.success(`${n} token(s) revogado(s) excluído(s)`, { position: 'top-right' });
    },
    onError: () => toast.error('Erro ao limpar revogados', { position: 'top-right' }),
  });

  const items = data?.data ?? [];
  const temRevogados = items.some(t => t.status === 'revogado');

  const pagination: IPagination = {
    page: 1,
    pages: 1,
    size: items.length,
    total: items.length,
    next_page: null,
    previous_page: null,
  };

  const columns: ColumnDef<IApiToken>[] = [
    {
      accessorKey: 'nome',
      header: 'Token',
      cell: ({ row }) => (
        <Flex align="center" gap={2}>
          <Key size={18} color={colors.primary} />
          <Text fontWeight="medium">{row.original.nome}</Text>
        </Flex>
      ),
    },
    {
      accessorKey: 'usuario_nome',
      header: 'Cliente',
      cell: ({ row }) =>
        row.original.usuario_nome ? (
          <Box>
            <Text fontSize="sm">{row.original.usuario_nome}</Text>
            {row.original.usuario_email && (
              <Text fontSize="xs" color="gray.400">
                {row.original.usuario_email}
              </Text>
            )}
          </Box>
        ) : (
          <Text color="gray.400" fontSize="sm">
            —
          </Text>
        ),
    },
    {
      accessorKey: 'external_ids',
      header: 'Empresas',
      cell: ({ row }) => {
        // multi-empresa: um badge por empresa (fallback p/ o singular legado)
        const ids = row.original.external_ids?.length
          ? row.original.external_ids
          : [row.original.external_id];
        const LIMITE = 2;
        const visiveis = ids.slice(0, LIMITE);
        const restantes = ids.length - visiveis.length;
        return (
          <Flex gap={1} align="center" maxW="220px">
            {visiveis.map(id => (
              <Badge key={id} colorScheme="blue" maxW="100px" isTruncated title={`Empresa ${id}`}>
                {`Empresa ${id}`}
              </Badge>
            ))}
            {restantes > 0 && (
              <Badge
                colorScheme="gray"
                variant="outline"
                cursor="pointer"
                flexShrink={0}
                onClick={() => setDetalhes(row.original)}
              >
                …+{restantes}
              </Badge>
            )}
          </Flex>
        );
      },
    },
    {
      accessorKey: 'colunas_por_tabela',
      header: 'Colunas liberadas',
      cell: ({ row }) => {
        // Fonte da verdade N-tabelas: {tabela: [colunas ON]}. Trunca pra não
        // esticar a linha — reticências horizontais (isTruncated por badge) e
        // verticais ("…+N tabela(s)"). O conjunto completo fica em
        // "Configurações" (TokenDetalhesModal), aberto ao clicar em qualquer "+N".
        const porTabela = row.original.colunas_por_tabela ?? {};
        const tabelas = Object.keys(porTabela).filter(t => porTabela[t].length > 0);
        if (tabelas.length === 0) {
          return (
            <Text color="gray.400" fontSize="sm">
              Nenhuma
            </Text>
          );
        }
        const multiTabela = tabelas.length > 1;
        const LIMITE_TABELAS = 2;
        const LIMITE_COLUNAS = 3;
        const tabelasVisiveis = tabelas.slice(0, LIMITE_TABELAS);
        const tabelasRestantes = tabelas.length - tabelasVisiveis.length;
        return (
          <Flex direction="column" gap={1} maxW="280px">
            {tabelasVisiveis.map(tabela => {
              const cols = porTabela[tabela];
              const colsVisiveis = cols.slice(0, LIMITE_COLUNAS);
              const colsRestantes = cols.length - colsVisiveis.length;
              return (
                <Flex key={tabela} gap={1} align="center">
                  {multiTabela && (
                    <Text
                      fontSize="xs"
                      color="gray.500"
                      fontWeight="semibold"
                      flexShrink={0}
                      maxW="80px"
                      isTruncated
                      title={tabela}
                    >
                      {tabela}:
                    </Text>
                  )}
                  {colsVisiveis.map(col => (
                    <Badge
                      key={`${tabela}.${col}`}
                      colorScheme="gray"
                      maxW="90px"
                      isTruncated
                      title={col}
                    >
                      {col}
                    </Badge>
                  ))}
                  {colsRestantes > 0 && (
                    <Badge
                      colorScheme="gray"
                      variant="outline"
                      cursor="pointer"
                      flexShrink={0}
                      onClick={() => setDetalhes(row.original)}
                    >
                      …+{colsRestantes}
                    </Badge>
                  )}
                </Flex>
              );
            })}
            {tabelasRestantes > 0 && (
              <Text
                fontSize="xs"
                color="gray.500"
                cursor="pointer"
                onClick={() => setDetalhes(row.original)}
              >
                ⋮ +{tabelasRestantes} tabela(s)
              </Text>
            )}
          </Flex>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge colorScheme={statusColor[row.original.status] ?? 'gray'}>
          {row.original.status}
        </Badge>
      ),
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const item = row.original;
        return (
          <ActionsButton aria-label="actions">
            <Button
              variant="unstyled"
              p="6px"
              display="flex"
              alignItems="center"
              gap={2}
              onClick={() => setDetalhes(item)}
            >
              <GearSix size={20} weight="bold" />
              <Text>Configurações</Text>
            </Button>
            <Button
              variant="unstyled"
              p="6px"
              display="flex"
              alignItems="center"
              gap={2}
              onClick={() => setTokenParaRevelar(item)}
            >
              <Eye size={20} weight="bold" />
              <Text>Revelar credenciais</Text>
            </Button>
            {item.status !== 'revogado' && (
              <Button
                variant="unstyled"
                p="6px"
                display="flex"
                alignItems="center"
                gap={2}
                onClick={() =>
                  showDialog({
                    status: 'warning',
                    title: 'Revogar este token?',
                    description: `${item.nome} — o token deixa de funcionar imediatamente. Ele continua na lista como "revogado" (você pode excluí-lo depois).`,
                    cancelLabel: 'Cancelar',
                    async onConfirm() {
                      await revogarToken(item.id);
                    },
                  })
                }
              >
                <Prohibit size={20} weight="bold" />
                <Text>Revogar</Text>
              </Button>
            )}
            {item.status === 'revogado' && (
              <Button
                variant="unstyled"
                p="6px"
                display="flex"
                alignItems="center"
                gap={2}
                color="red.500"
                onClick={() =>
                  showDialog({
                    status: 'warning',
                    title: 'Excluir este token?',
                    description: `${item.nome} — o token será removido da lista (soft-delete). Só tokens revogados podem ser excluídos.`,
                    cancelLabel: 'Cancelar',
                    async onConfirm() {
                      await excluirToken(item.id);
                    },
                  })
                }
              >
                <Trash size={20} weight="bold" />
                <Text>Excluir</Text>
              </Button>
            )}
          </ActionsButton>
        );
      },
    },
  ];

  if (isError) {
    return (
      <Flex direction="column" align="center" gap={2} py={10}>
        <Text fontWeight="medium" color="red.500">
          Falha ao carregar os tokens
        </Text>
        <Text color="gray.500" fontSize="sm">
          Verifique a conexão e tente recarregar.
        </Text>
        <Button size="sm" onClick={() => invalidar()}>
          Recarregar
        </Button>
      </Flex>
    );
  }

  return (
    <Box>
      {items.length === 0 && !isFetching ? (
        <EmptyData
          title="Nenhum token cadastrado"
          subtitle="Crie um token para um cliente consumir a API pública"
          buttonLabel="Criar token"
          onClick={() => setIsFormOpen(true)}
          icon={<Key size={90} color={colors.primary} />}
        />
      ) : (
        <TableBox>
          <Flex justify="flex-end" gap={2}>
            {temRevogados && (
              <Button
                variant="outline"
                colorScheme="red"
                leftIcon={<TrashSimple weight="bold" />}
                onClick={() =>
                  showDialog({
                    status: 'warning',
                    title: 'Limpar todos os tokens revogados?',
                    description:
                      'Todos os tokens revogados serão removidos da lista (soft-delete). Os ativos/suspensos não são afetados.',
                    cancelLabel: 'Cancelar',
                    async onConfirm() {
                      await limparRevogados();
                    },
                  })
                }
              >
                Limpar revogados
              </Button>
            )}
            <Button leftIcon={<Plus weight="bold" />} onClick={() => setIsFormOpen(true)}>
              Criar token
            </Button>
          </Flex>

          <div className="border border-b-divider" />

          <DataTable<IApiToken>
            columns={columns}
            data={items}
            pagination={pagination}
            emptyMsg="Nenhum token encontrado."
            loadingMsg="Carregando tokens..."
            isFetching={isFetching}
          />
        </TableBox>
      )}

      <TokenFormModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onCreated={() => queryClient.invalidateQueries({ queryKey: ['api-controller-tokens'] })}
      />

      <RevelarTokenModal token={tokenParaRevelar} onClose={() => setTokenParaRevelar(null)} />

      <TokenDetalhesModal token={detalhes} onClose={() => setDetalhes(null)} />
    </Box>
  );
}
